import { useColorMode } from "@kobalte/core/color-mode";
import { createQuery, useQueryClient } from "@tanstack/solid-query";
import { createFileRoute, Link } from "@tanstack/solid-router";
import type { ChartData, ChartOptions } from "chart.js";
import { createMemo, createSignal, For, Show, Suspense } from "solid-js";
import {
  AccountBalanceCard,
  type DashboardAccountType,
} from "~/components/account/account-balance-card";
import { AddBusinessForm } from "~/components/add-business-dialog";
import { UpgradePromptDialog } from "~/components/document/upgrade-prompt-dialog";
import {
  IconArrowRight,
  IconBuilding2,
  IconCalculator,
  IconCircleDollarSign,
  IconFiles,
  IconPlus,
} from "~/components/icons";
import { Button } from "~/components/ui/button";
import { BarChart } from "~/components/ui/charts";
import { Skeleton } from "~/components/ui/skeleton";
import { AccountOverviewResource } from "~/features/account/account-overview.resource";
import {
  businessesResource,
  useActiveBusinessId,
} from "~/features/business/business.resource";
import { getDashboardPageData } from "~/features/dashboard/dashboard-page";
import { DocumentsResource } from "~/features/document/document.resource";
import { useDocumentUpload } from "~/features/document/use-document-upload";
import {
  DepreciationOverviewResource,
  type DepreciationOverviewData,
} from "~/features/fixed-assets/depreciation-overview.resource";
import { JournalEntriesResource } from "~/features/journal-entry/journal-entry.resource";
import type { BusinessSummary } from "~/lib/business-store";
import {
  formatCurrency,
  formatDisplayCalendarDate,
  formatDisplayDate,
} from "~/lib/utils";

const BALANCE_SHEET_TYPES: DashboardAccountType[] = [
  "asset",
  "equity",
  "liability",
];

//const INCOME_STATEMENT_TYPES: DashboardAccountType[] = ["revenue", "expense"];

const RECENT_LIMIT = 5;

export const Route = createFileRoute("/_dashboard/dashboard/")({
  staticData: { breadcrumb: "" },
  loader: async ({ context }) => {
    const collection = await context.queryClient.ensureQueryData(
      businessesResource.options(undefined),
    );
    const businessId = collection.activeBusinessId;
    if (!businessId) return { businessId: null } as const;
    const data = await getDashboardPageData({ data: { businessId } });
    AccountOverviewResource.seed(
      context.queryClient,
      { businessId },
      data.accountOverview,
    );
    DepreciationOverviewResource.seed(
      context.queryClient,
      { businessId },
      data.depreciationOverview,
    );
    // seedIfEmpty (not seed) so fresh client-side optimistic mutations (e.g. a
    // renamed document/JE) aren't clobbered by stale loader data.
    JournalEntriesResource.seedIfEmpty(
      context.queryClient,
      { businessId, limit: RECENT_LIMIT },
      data.recentJournalEntries,
    );
    DocumentsResource.seedIfEmpty(
      context.queryClient,
      { businessId, limit: RECENT_LIMIT },
      data.recentDocuments,
    );
    return { businessId, ...data };
  },
  component: DashboardOverview,
});

function DashboardOverview() {
  const qc = useQueryClient();
  const loaderData = Route.useLoaderData();
  const businessCollection = businessesResource.use(() => undefined);
  const businessId = useActiveBusinessId();
  const hasActiveBusiness = () => !!businessCollection.data?.activeBusinessId;
  const { uploading, upgradeDialog, setUpgradeDialog, handleAddDocument } =
    useDocumentUpload();

  // Seed caches from loader data so client-side queries don't re-fetch
  const ld = loaderData();
  if (ld?.businessId) {
    AccountOverviewResource.seedIfEmpty(
      qc,
      { businessId: ld.businessId },
      ld.accountOverview,
    );
    DepreciationOverviewResource.seedIfEmpty(
      qc,
      { businessId: ld.businessId },
      ld.depreciationOverview,
    );
    JournalEntriesResource.seedIfEmpty(
      qc,
      { businessId: ld.businessId, limit: RECENT_LIMIT },
      ld.recentJournalEntries,
    );
    DocumentsResource.seedIfEmpty(
      qc,
      { businessId: ld.businessId, limit: RECENT_LIMIT },
      ld.recentDocuments,
    );
  }

  const accountOverview = AccountOverviewResource.use(
    () => ({ businessId: businessId() ?? "" }),
    () => !!businessId(),
  );

  const depreciationOverview = DepreciationOverviewResource.use(
    () => ({ businessId: businessId() ?? "" }),
    () => !!businessId(),
  );

  const recentJournalEntries = createQuery(() => ({
    ...JournalEntriesResource.options(
      { businessId: businessId() ?? "", limit: RECENT_LIMIT },
      !!businessId(),
    ),
  }));

  const recentDocuments = createQuery(() => ({
    ...DocumentsResource.options(
      { businessId: businessId() ?? "", limit: RECENT_LIMIT },
      !!businessId(),
    ),
  }));

  function handleBusinessCreated(business: BusinessSummary) {
    const queryOpts = businessesResource.options(undefined);
    qc.setQueryData(queryOpts.queryKey, (current) => {
      if (current)
        return {
          ...current,
          businesses: [...current.businesses, business],
          activeBusinessId: business.id,
        };
      return { businesses: [business], activeBusinessId: business.id };
    });
  }

  return (
    <div class="p-6">
      <Show
        when={hasActiveBusiness()}
        fallback={
          <div class="flex min-h-[60vh] flex-col items-center justify-center">
            <div class="flex w-full max-w-xl flex-col items-center gap-6">
              <div class="flex flex-col items-center gap-3 text-center">
                <div class="flex size-20 items-center justify-center rounded-2xl border border-border bg-muted/50">
                  <IconBuilding2 class="size-10 text-muted-foreground opacity-60" />
                </div>
                <div class="space-y-1">
                  <h2 class="text-2xl font-semibold tracking-tight">
                    Let's start reducing your taxes!
                  </h2>
                  <p class="text-sm text-muted-foreground">
                    Create your first business to get started.
                  </p>
                </div>
              </div>
              <div class="w-full rounded-xl border border-border bg-card p-6 shadow-sm">
                <AddBusinessForm
                  onSuccess={handleBusinessCreated}
                  submitLabel="Create Business"
                />
              </div>
            </div>
          </div>
        }
      >
        <div class="flex flex-col gap-8">
          {/* Header */}
          <div>
            <h1 class="text-2xl font-bold">Overview</h1>
            <p class="text-sm text-muted-foreground">
              Activity across your accounts.
            </p>
          </div>

          {/* Balance sheet */}
          <section class="flex flex-col gap-3">
            {/* <h2 class="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Balance Sheet
            </h2> */}
            <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
              {BALANCE_SHEET_TYPES.map((type) => (
                <AccountBalanceCard
                  type={type}
                  businessId={businessId() ?? undefined}
                  overview={accountOverview.data?.byType[type]}
                />
              ))}
            </div>
          </section>

          {/* Recent activity */}
          <section class="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Recent Journal Entries */}
            <div class="relative flex flex-col overflow-hidden rounded-2xl border bg-card shadow-sm">
              <div
                class="h-0.5 w-full shrink-0"
                style={{ background: "var(--color-account-asset)" }}
              />
              <div class="flex items-center gap-2.5 px-5 pb-3 pt-5">
                <div
                  class="flex size-8.5 shrink-0 items-center justify-center rounded-[10px]"
                  style={{
                    background: "rgba(91,141,239,0.15)",
                    color: "var(--color-account-asset)",
                  }}
                >
                  <IconCircleDollarSign size={16} />
                </div>
                <span class="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
                  Recent Journal Entries
                </span>
                <Link
                  to="/dashboard/journal-entries/$journalEntryId"
                  params={{ journalEntryId: "new" }}
                  class="ml-auto"
                  aria-label="Add journal entry"
                >
                  <Button
                    variant="ghost"
                    size="icon"
                    class="size-7 text-muted-foreground hover:text-foreground"
                  >
                    <IconPlus class="size-4" />
                  </Button>
                </Link>
              </div>
              <Suspense
                fallback={
                  <div class="flex flex-col gap-px px-5 pb-2">
                    <For each={[1, 2, 3, 4, 5]}>
                      {() => (
                        <div class="flex items-center justify-between py-2.5">
                          <div class="flex flex-col gap-1.5">
                            <Skeleton animate class="h-3.5 w-32 rounded" />
                            <Skeleton animate class="h-3 w-20 rounded" />
                          </div>
                          <Skeleton animate class="h-3.5 w-16 rounded" />
                        </div>
                      )}
                    </For>
                  </div>
                }
              >
                <Show
                  when={(recentJournalEntries.data?.items.length ?? 0) > 0}
                  fallback={
                    <p class="px-5 pb-4 text-sm text-muted-foreground">
                      No journal entries yet.
                    </p>
                  }
                >
                  <ul class="divide-y divide-border">
                    <For each={recentJournalEntries.data?.items ?? []}>
                      {(je) => {
                        const amount = je.amount;
                        return (
                          <li>
                            <Link
                              to="/dashboard/journal-entries/$journalEntryId"
                              params={{ journalEntryId: je.id }}
                              class="flex items-center justify-between gap-4 px-5 py-2.5 hover:bg-muted/50 transition-colors"
                            >
                              <div class="min-w-0 flex-1">
                                <p class="truncate text-sm font-medium leading-tight">
                                  {je.description || "—"}
                                </p>
                                <p class="mt-0.5 text-[11px] text-muted-foreground">
                                  {formatDisplayCalendarDate(je.date, {
                                    style: "short",
                                  }) ?? "—"}
                                </p>
                              </div>
                              <span class="shrink-0 font-mono text-sm font-medium">
                                {formatCurrency(amount)}
                              </span>
                            </Link>
                          </li>
                        );
                      }}
                    </For>
                  </ul>
                </Show>
              </Suspense>
              <div class="mt-auto flex items-center justify-between border-t px-5 py-2.5">
                <span class="text-[11px] tracking-[0.04em] text-muted-foreground">
                  {recentJournalEntries.data
                    ? `Showing ${recentJournalEntries.data.items.length} of ${recentJournalEntries.data.total}`
                    : `Latest ${RECENT_LIMIT}`}
                </span>
                <Link
                  to="/dashboard/journal-entries"
                  class="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
                >
                  View all
                  <IconArrowRight class="size-3" />
                </Link>
              </div>
            </div>

            {/* Recent Documents */}
            <div class="relative flex flex-col overflow-hidden rounded-2xl border bg-card shadow-sm">
              <div
                class="h-0.5 w-full shrink-0"
                style={{ background: "var(--color-account-equity)" }}
              />
              <div class="flex items-center gap-2.5 px-5 pb-3 pt-5">
                <div
                  class="flex size-8.5 shrink-0 items-center justify-center rounded-[10px]"
                  style={{
                    background: "rgba(62,207,174,0.15)",
                    color: "var(--color-account-equity)",
                  }}
                >
                  <IconFiles size={16} />
                </div>
                <span class="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
                  Recent Documents
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  class="ml-auto size-7 text-muted-foreground hover:text-foreground"
                  aria-label="Add documents"
                  disabled={uploading()}
                  onClick={handleAddDocument}
                >
                  <IconPlus class="size-4" />
                </Button>
              </div>
              <Suspense
                fallback={
                  <div class="flex flex-col gap-px px-5 pb-2">
                    <For each={[1, 2, 3, 4, 5]}>
                      {() => (
                        <div class="flex items-center justify-between py-2.5">
                          <div class="flex flex-col gap-1.5">
                            <Skeleton animate class="h-3.5 w-40 rounded" />
                            <Skeleton animate class="h-3 w-16 rounded" />
                          </div>
                          <Skeleton animate class="h-5 w-16 rounded" />
                        </div>
                      )}
                    </For>
                  </div>
                }
              >
                <Show
                  when={(recentDocuments.data?.items.length ?? 0) > 0}
                  fallback={
                    <p class="px-5 pb-4 text-sm text-muted-foreground">
                      No documents yet.
                    </p>
                  }
                >
                  <ul class="divide-y divide-border">
                    <For each={recentDocuments.data?.items ?? []}>
                      {(doc) => {
                        return (
                          <li>
                            <Link
                              to="/dashboard/documents/$documentId"
                              params={{ documentId: doc.id }}
                              class="flex items-center justify-between gap-4 px-5 py-2.5 hover:bg-muted/50 transition-colors"
                            >
                              <div class="min-w-0 flex-1">
                                <p class="truncate text-sm font-medium leading-tight">
                                  {doc.name}
                                </p>
                                <p class="mt-0.5 text-[11px] text-muted-foreground">
                                  {doc.date
                                    ? (formatDisplayDate(doc.date, {
                                        style: "short",
                                        timeZone: "UTC",
                                      }) ?? "—")
                                    : (formatDisplayDate(doc.createdAt, {
                                        style: "short",
                                      }) ?? "—")}
                                  {doc.amount != null
                                    ? ` · ${formatCurrency(doc.amount)}`
                                    : ""}
                                </p>
                              </div>
                            </Link>
                          </li>
                        );
                      }}
                    </For>
                  </ul>
                </Show>
              </Suspense>
              <div class="mt-auto flex items-center justify-between border-t px-5 py-2.5">
                <span class="text-[11px] tracking-[0.04em] text-muted-foreground">
                  {recentDocuments.data
                    ? `Showing ${recentDocuments.data.items.length} of ${recentDocuments.data.total}`
                    : `Latest ${RECENT_LIMIT}`}
                </span>
                <Link
                  to="/dashboard/documents"
                  class="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
                >
                  View all
                  <IconArrowRight class="size-3" />
                </Link>
              </div>
            </div>
          </section>

          {/* Depreciation overview */}
          <section>
            <DepreciationCard data={depreciationOverview.data} />
          </section>
        </div>
      </Show>

      <UpgradePromptDialog
        open={upgradeDialog().open}
        message={upgradeDialog().message}
        onOpenChange={(open) => setUpgradeDialog((s) => ({ ...s, open }))}
      />
    </div>
  );
}

const DEPRECIATION_COLORS = [
  "rgba(91,141,239,0.85)", // blue
  "rgba(240,122,106,0.85)", // red
  "rgba(62,207,174,0.85)", // teal
  "rgba(255,196,92,0.85)", // amber
  "rgba(163,132,247,0.85)", // purple
];

type YearRangeOption = 5 | 10 | 999;

function DepreciationCard(props: {
  data: DepreciationOverviewData | undefined;
}) {
  const { colorMode } = useColorMode();
  const [yearRange, setYearRange] = createSignal<YearRangeOption>(5);

  const chartOptions = createMemo<ChartOptions>(() => {
    const dark = colorMode() === "dark";
    const tickColor = dark ? "hsl(240, 5%, 64.9%)" : "hsl(240, 3.8%, 30%)";
    const gridColor = dark
      ? "rgba(255,255,255,0.07)"
      : "rgba(128,128,128,0.08)";
    return {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          stacked: true,
          border: { display: false },
          grid: { display: false },
          ticks: {
            color: tickColor,
            font: { size: 11, family: "'DM Mono', monospace" } as any,
            maxRotation: 0,
          },
        },
        y: {
          stacked: true,
          display: true,
          border: { display: false },
          grid: { color: gridColor },
          ticks: {
            maxTicksLimit: 4,
            color: tickColor,
            font: { size: 11, family: "'DM Mono', monospace" } as any,
            callback: (v: number | string) => {
              const n = +v;
              if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
              if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}k`;
              return `$${n}`;
            },
          },
        },
      },
      plugins: {
        legend: {
          display: true,
          position: "bottom" as const,
          labels: {
            usePointStyle: true,
            pointStyle: "circle" as const,
            boxWidth: 6,
            boxHeight: 6,
            color: tickColor,
            font: { size: 12, family: "'DM Mono', monospace" } as any,
            padding: 20,
          },
        },
        tooltip: {
          enabled: true,
          backgroundColor: dark
            ? "rgba(50,50,60,0.95)"
            : "rgba(255,255,255,0.96)",
          titleColor: dark ? "#fff" : "hsl(240, 10%, 10%)",
          bodyColor: dark ? "#fff" : "hsl(240, 10%, 10%)",
          borderColor: dark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)",
          borderWidth: 1,
          padding: 8,
          cornerRadius: 6,
          callbacks: {
            label: (ctx) =>
              ` ${ctx.dataset.label}: $${(ctx.parsed.y ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`,
          },
        },
      },
    };
  });

  const assetCount = () => props.data?.assetCount ?? 0;
  const hasData = () => (props.data?.yearlyDepreciation?.length ?? 0) > 0;

  // Current year total from the data
  const currentYearTotal = createMemo(() => {
    const yearly = props.data?.yearlyDepreciation;
    if (!yearly) return 0;
    const cy = new Date().getFullYear();
    return yearly.find((e) => e.year === cy)?.total ?? 0;
  });

  // Filter yearly entries to N years starting from current year
  const filteredYearly = createMemo(() => {
    const yearly = props.data?.yearlyDepreciation;
    if (!yearly) return [];
    const range = yearRange();
    if (range === 999) return yearly;
    const currentYear = new Date().getFullYear();
    const startIdx = yearly.findIndex((e) => e.year >= currentYear);
    if (startIdx === -1) return [];
    return yearly.slice(startIdx, startIdx + range);
  });

  const chartData = createMemo<ChartData>(() => {
    const yearly = filteredYearly();
    if (yearly.length === 0) {
      return { labels: [], datasets: [] };
    }

    // Collect ALL methods from the full dataset so legend stays consistent
    const fullYearly = props.data?.yearlyDepreciation;
    const allMethods = new Set<string>();
    const methodSource = fullYearly ?? yearly;
    for (const entry of methodSource) {
      for (const method of Object.keys(entry.byMethod)) {
        allMethods.add(method);
      }
    }
    const methods = [...allMethods].sort();

    return {
      labels: yearly.map((e) => String(e.year)),
      datasets: methods.map((method, i) => ({
        label: method,
        data: yearly.map((e) => e.byMethod[method] ?? 0),
        backgroundColor: DEPRECIATION_COLORS[i % DEPRECIATION_COLORS.length],
        borderRadius: 2,
        borderSkipped: false,
      })),
    };
  });

  const rangeOptions: { label: string; value: YearRangeOption }[] = [
    { label: "5y", value: 5 },
    { label: "10y", value: 10 },
    { label: "All", value: 999 },
  ];

  return (
    <section class="relative flex flex-col overflow-hidden rounded-2xl border bg-card text-card-foreground shadow-sm">
      {/* Accent bar */}
      <div
        class="h-0.5 w-full shrink-0"
        style={{ background: DEPRECIATION_COLORS[0] }}
      />

      {/* Header + summary stats */}
      <div class="px-5 pb-3 pt-5">
        <div class="mb-3.5 flex items-center justify-between">
          <div class="flex items-center gap-2.5">
            <div
              class="flex size-8.5 shrink-0 items-center justify-center rounded-[10px]"
              style={{
                background: "rgba(91,141,239,0.15)",
                color: "rgba(91,141,239,1)",
              }}
            >
              <IconCalculator size={16} />
            </div>
            <span class="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
              Depreciation
            </span>
          </div>

          {/* Year range selector */}
          <div class="flex items-center gap-0.5 rounded-lg border p-0.5">
            <For each={rangeOptions}>
              {(opt) => (
                <button
                  type="button"
                  onClick={() => setYearRange(opt.value)}
                  classList={{
                    "rounded-md px-2.5 py-1 text-[11px] font-medium transition-colors": true,
                    "bg-accent text-accent-foreground shadow-xs":
                      yearRange() === opt.value,
                    "text-muted-foreground hover:text-foreground":
                      yearRange() !== opt.value,
                  }}
                >
                  {opt.label}
                </button>
              )}
            </For>
          </div>
        </div>

        <div class="flex items-center gap-2">
          <span class="font-mono text-[26px] font-medium tracking-[-0.02em]">
            {formatCurrency(currentYearTotal())}
          </span>
          <span class="text-[11px] tracking-[0.04em] text-muted-foreground">
            available this year &middot; {assetCount()} asset
            {assetCount() !== 1 ? "s" : ""}
          </span>
        </div>
      </div>

      {/* Chart */}
      <Show when={hasData()}>
        <div class="h-72 w-full px-5 pb-5">
          <BarChart data={chartData()} options={chartOptions()} />
        </div>
      </Show>
    </section>
  );
}
